import { Component, Input, OnInit } from "@angular/core";
import ActionTypesEnum from "@shared/enums/action-constant.enum";
import { createFormControl, requestObjectMapper, validateFormGroup } from "@shared/utils/form.util";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { CustomEventService } from "@shared/services/custom-event.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { DatasourceService } from "@shared/services/datasource.service";
import { GitwiserService } from "@shared/services/gitwiser.service";

@UntilDestroy()
@Component({
  selector: 'app-add-deployment-analytics',
  templateUrl: './add-deployment-analytics.component.html'
})
export class AddDeploymentAnalyticsComponent implements OnInit {

  @Input() loading: boolean;
  @Input() deploymentAnalyticsForm: boolean;

  fields: Widget.Field[] = [];
  isLoading: boolean;

  addDeploymentAnalyticForm: FormGroup;
  analysisId: any;
  addonsDeployment: any;
  selectedAddon: any;
  formItemList: any;
  fieldItemList: any;

  deploymentAnalyticsPatterns = [];
  hotfixPatternList: { label: string; value: string }[] = [];

  selectedFields: Map<string, any> = new Map();
  deploymentAnalyticsApmFields: Widget.Field[] = [];
  resetInputs = false;

  constructor(private customEventService: CustomEventService,
              private gitwiserService: GitwiserService,
              private dataSourceService: DatasourceService,
              private fb: FormBuilder) {
  }

  setSelectedFieldData = (fields: any[] = []): void => {

    fields.forEach(field => {
      let value = null;

      const selections = field?.data?.selections;

      if (selections) {
        value = selections instanceof Array ?
          selections.map(item => item.key) :
          selections instanceof Object ? selections.key : selections;
      }

      this.selectedFields.set(field.key, value);
    });

  };

  ngOnInit(): void {
    this.addDeploymentAnalyticForm = this.fb.group({
      deploymentAnalyticsAddons: [null, [Validators.required]]
    });

    this.hotfixPatternList.push(
      { label: 'hotfix', value: 'hotfix' },
      { label: 'incident', value: 'incident' },
      { label: 'error', value: 'error' });
  }

  selectField(value, field): void {
    this.resetInputs = false;
    this.selectedFields.set(field.key, value);

    if (field.key === 'deploymentAnalyticsAddons') {
      this.gitwiserService.fields(value).subscribe(response => {
        this.fields = response.fields;
        this.fields = this.fields.concat(response.fields);
        response.fields.forEach(item => {
          const formControl = createFormControl(null, item.validators && item.validators.indexOf('required') > -1? Validators['required'] : null);
          this.addDeploymentAnalyticForm.addControl(item.key, formControl);
        })
        this.customEventService.announceEvent(ActionTypesEnum.ANALYSIS_CREATE_FIELD_SELECTED, {
          field,
          form: this.addDeploymentAnalyticForm,
          value
        });
      });
    } else if (field.key === 'deploymentAnalyticsIncidentSource') {
      if (this.addDeploymentAnalyticForm.controls.deploymentAnalyticsIncidentSource?.value !== null){
        this.deploymentAnalyticsApmFields.forEach(item => {
          this.addDeploymentAnalyticForm.removeControl(item.key);
        });
        this.deploymentAnalyticsApmFields = [];
        this.clearDeploymentAnalyticsIncidentSource()
        this.gitwiserService.deploymentAnalyticsApmFields(value).subscribe(response => {
          this.deploymentAnalyticsApmFields = response.fields;
          this.fields = this.fields.concat(response.fields);
          response.fields.forEach(item => {
            const formControl = createFormControl(null, item.validators && item.validators.indexOf('required') > -1 ? Validators['required'] : null);
            this.addDeploymentAnalyticForm.addControl(item.key, formControl);
          })
          this.customEventService.announceEvent(ActionTypesEnum.ANALYSIS_CREATE_FIELD_SELECTED, {
            field,
            form: this.addDeploymentAnalyticForm,
            value
          });
        });
      }else {
       this.clearDeploymentAnalyticsIncidentSource()
      }
    } else {
      this.customEventService.announceEvent(ActionTypesEnum.ANALYSIS_CREATE_FIELD_SELECTED, {
        field,
        form: this.addDeploymentAnalyticForm,
        value
      });
    }
  }


  clearDeploymentAnalyticsIncidentSource():void{
    this.fields.map(item => {
      if (item.name === 'Applications') {
        this.fields.splice(-2);
        this.addDeploymentAnalyticForm.get("deploymentAnalyticsIncidentSource").clearValidators();
        this.addDeploymentAnalyticForm.removeControl("deploymentAnalyticsIncidentSourceDatasource");
        this.addDeploymentAnalyticForm.removeControl("deploymentAnalyticsIncidentApplication");
      }
    })
  }

  getDataSourcesDeployment($event?: any): void {
    this.isLoading = true
    this.getAddonsDeployment();
    if ($event === undefined) {
      return
    } else {
      this.gitwiserService.deploymentAnalyticsFields($event?.key,false).pipe().subscribe(item => {
        const selectedAddonField: any = {
          key: 'deploymentAnalyticsAddons',
          data: { selections: $event.key },
          affects: [item?.fields[0]?.key]
        };

        this.fields = [selectedAddonField];
        this.fields = this.fields.concat(item.fields);
        this.fields.forEach(field => {
          this.addDeploymentAnalyticForm.addControl(field.key, new FormControl('', field.validators && field.validators.indexOf('required') > -1 ? Validators.required :[]))
          field.required = field.validators?.find(data => data === "required");
          const regex = field.validators?.find(data => data.startsWith("regex::"));

          if (regex) {
            field.regex = regex.substring(7, regex.length);
          }

          this.setSelectedFieldData(this.fields);
        })
        this.customEventService.announceEvent(ActionTypesEnum.ANALYSIS_CREATE_FIELD_SELECTED, {
          field: selectedAddonField,
          form: this.addDeploymentAnalyticForm,
          value: selectedAddonField.key
        });
      })
    }
    this.isLoading = false;
  }

  getAddonsDeployment(): void {
    this.gitwiserService.getAddonsDeployment()
      .pipe(untilDestroyed(this))
      .subscribe(response => {
        this.addonsDeployment = response;
      }, error => {
        console.log("error occurred while fetching initial data for sprints", error);
      });
  }

  submitForm(): void {
    this.formItemList = Object.values(Array.from(new Set(Object.keys(this.addDeploymentAnalyticForm.controls).map(item => item))));
    this.fieldItemList = Object.values(Array.from(new Set(this.fields.map(item => item.key))));
    this.formItemList = this.formItemList.filter(val => !this.fieldItemList.includes(val));
    if (this.fieldItemList.length){
      this.addDeploymentAnalyticForm.removeControl(this.formItemList[0]);
    }
    this.addDeploymentAnalyticForm.removeControl(this.formItemList[1]);
    this.addDeploymentAnalyticForm.removeControl(this.formItemList[2]);
    this.addDeploymentAnalyticForm.removeControl(this.formItemList[3]);
    this.addDeploymentAnalyticForm.removeControl(this.formItemList[4]);
    this.addDeploymentAnalyticForm.removeControl(this.formItemList[5]);

    if (!this.addDeploymentAnalyticForm.controls.deploymentParameterValue?.value) {
      this.addDeploymentAnalyticForm.get('deploymentParameterValue')?.clearValidators();
    }

    if (!this.addDeploymentAnalyticForm.controls.deploymentCloneURLVariableKey?.value) {
      this.addDeploymentAnalyticForm.get('deploymentCloneURLVariableKey')?.clearValidators();
    }

    if (!this.addDeploymentAnalyticForm.controls.deploymentAnalyticsRepository?.value) {
      this.addDeploymentAnalyticForm.get('deploymentAnalyticsRepository')?.clearValidators();
    }

    if (!this.addDeploymentAnalyticForm.controls.deploymentCommitVariableKey?.value) {
      this.addDeploymentAnalyticForm.get('deploymentCommitVariableKey')?.clearValidators();
    }

    if (this.addDeploymentAnalyticForm.controls.deploymentAnalyticsIncidentSource?.value === null ||
      this.addDeploymentAnalyticForm.controls.deploymentAnalyticsIncidentSource?.value === undefined
      || this.addDeploymentAnalyticForm.controls.deploymentAnalyticsIncidentSource?.value === '') {
      this.addDeploymentAnalyticForm.get('deploymentAnalyticsIncidentSource')?.clearValidators();
      this.addDeploymentAnalyticForm.removeControl('deploymentAnalyticsIncidentSource');
      this.addDeploymentAnalyticForm.removeControl('deploymentAnalyticsIncidentSourceDatasource');
      this.addDeploymentAnalyticForm.removeControl('deploymentAnalyticsIncidentApplication');
    }

    if (this.addDeploymentAnalyticForm?.get('deploymentAnalyticsReleaseStrategy')?.value!=='GITFLOW_RELEASE') {
      this.addDeploymentAnalyticForm.removeControl("deploymentAnalyticsReleasePattern");
    }

    if(this.addDeploymentAnalyticForm.controls.deploymentAnalyticsWorkflows?.value === "" ||
      this.addDeploymentAnalyticForm.controls.deploymentAnalyticsWorkflows?.value === undefined){
      this.addDeploymentAnalyticForm.removeControl("deploymentAnalyticsRepositories");
      this.addDeploymentAnalyticForm.removeControl("deploymentAnalyticsOrganizations");
      this.addDeploymentAnalyticForm.removeControl("deploymentAnalyticsWorkflows");
     }

    if(this.addDeploymentAnalyticForm.controls.deploymentAnalyticsInitialCommit?.value === "" ||
    this.addDeploymentAnalyticForm.controls.deploymentAnalyticsInitialCommit?.value === undefined){
    this.addDeploymentAnalyticForm.removeControl("deploymentAnalyticsInitialCommit");
   }

    validateFormGroup(this.addDeploymentAnalyticForm);

    if (this.addDeploymentAnalyticForm.invalid) {
      return;
    }

    const requestData: any = {
      formData: this.addDeploymentAnalyticForm.value,
      deploymentAnalyticsPatterns: this.deploymentAnalyticsPatterns,
    };

    requestData.formData = requestObjectMapper(requestData.formData, this.fields);

    for (const key of Object.keys(requestData.formData)) {
      if (requestData.formData[key] && !(requestData.formData[key] instanceof Array)
        && key !== 'from' && !requestData.formData[key].key) {
        requestData.formData[key] = {
          key: requestData.formData[key],
          label: requestData.formData[key]
        };
      }
    }

    if ((requestData.formData["deploymentAnalyticsIncidentSource"]?.key) === undefined) {
      delete requestData.formData["deploymentAnalyticsIncidentSource"];
    }

    if ((requestData.formData["deploymentAnalyticsRepository"]?.key) === undefined) {
      delete requestData.formData["deploymentAnalyticsRepository"];
    }

    if((requestData.formData["deploymentAnalyticsWorkflows"]?.key) === undefined) {
      delete requestData.formData["deploymentAnalyticsRepositories"];
      delete requestData.formData["deploymentAnalyticsOrganizations"];
      delete requestData.formData["deploymentAnalyticsWorkflows"];
    }

    this.customEventService.announceEvent(ActionTypesEnum.ADD_DEPLOYMENT_ANALYTICS, requestData);
  }

  cancelForm(): void {
    this.customEventService.announceEvent(ActionTypesEnum.CLOSE_MODAL);
  }

  searchSelect(value: string, field: any) {
      this.resetInputs = false;
       const foundValue=  value.length > 0 ?
        field.data.contentAll.filter(item => item.label.toLocaleLowerCase().includes(value.toLocaleLowerCase())).slice(0, 500)
        : field.data.contentAll ? field.data.contentAll.slice(0, 500) : field.data.content;
        if(foundValue && foundValue.length>0){
          field.data.content=foundValue
          return;
        }
    field.loading = false;
    if (field?.widgetFieldListener?.eventType !== 'KEYUP') {
      return;
    }
    if (value.length < 3) {  field.data.content = field.data.defaultContent; return; }

    this.customEventService.announceEvent(ActionTypesEnum.ANALYSIS_CREATE_FIELD_KEYUP_ACTION, {field, form: this.addDeploymentAnalyticForm, value});
  }

  resetForm(): void {
    this.addDeploymentAnalyticForm?.reset();
    this.resetInputs = true;
  }
}
